/**
 * Where an invite link with intent lands.
 *
 * The flow, matching the Small Group invite screen it deliberately resembles:
 * open link → sign in (the auth gate handles that; this route is reachable only
 * once signed in) → SEE who sent it → tap accept → their Cue arrives in your
 * requests, where Accept and Ignore are exactly what they always were.
 *
 * Nothing here starts a conversation. The link creates a REQUEST, and the
 * person who opened it answers it the same way they would answer a stranger's —
 * which is the whole reason this mints a Cue rather than inventing a second,
 * friendlier door that would have to be kept in agreement with the first.
 *
 * A link that cannot be claimed gets a sentence, not a failure. Someone who
 * opens a link their friend already used, or one from last month, has done
 * nothing wrong and should be told which of those it was — an invitation that
 * resolves to a blank error is indistinguishable from a broken app.
 */
import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { ApiError, invitesApi } from "@/api";
import { Avatar } from "@/components/ui/avatar";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { referralStrings as S } from "@/i18n/features/referral.i18n";
import { notify } from "@/lib/dialog";
import { isMobileUserAgent, landingOriginForHost } from "@/lib/mobile-web";
import type { InvitePreview, InviteState } from "@/types";

/**
 * A phone's browser, where this screen becomes a get-the-app interstitial:
 * the web app is not built for that viewport, and a browser on a device the
 * app runs on means the app is not installed. The guard in
 * `use-auth-routing.ts` exempts this route on mobile web for the same reason,
 * so a signed-out recipient sees the invitation, not a login wall.
 */
const MOBILE_WEB =
  Platform.OS === "web" &&
  typeof navigator !== "undefined" &&
  isMobileUserAgent(navigator.userAgent ?? "");

/**
 * The one sentence a dead link gets. `ok` is absent on purpose — it is not a
 * refusal and has no message.
 */
const REFUSAL_COPY: Record<Exclude<InviteState, "ok">, string> = {
  expired: S.landingExpired,
  already_used: S.landingUsed,
  your_own: S.landingOwn,
};

export default function InviteLandingScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const { code } = useLocalSearchParams<{ code: string }>();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(async () => {
    if (!code) return;
    try {
      setPreview(await invitesApi.preview(code));
      setMissing(false);
    } catch (error) {
      // 404 is a code that never existed; anything else is transient. Both land
      // on a message rather than an empty screen.
      setMissing(error instanceof ApiError && error.status === 404);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    void load();
  }, [load]);

  async function accept() {
    if (!code || accepting) return;
    setAccepting(true);
    try {
      const result = await invitesApi.redeem(code);
      if (result.outcome === "cue_sent" && result.cueId) {
        // Into the requests list, where their Cue now is. Saying "accepted" and
        // leaving them on this screen would give them nothing to do with it.
        notify(t(S.landingCueArrived));
        router.replace("/(tabs)/cues");
        return;
      }
      if (result.outcome === "already_connected") {
        // They already talk. No Cue was minted and none should have been — the
        // way back into a conversation is the conversation.
        //
        // The thread may not have survived: `connected_at` is permanent while
        // the conversation it records is not, so a pair can be connected with
        // nothing left to open. Both branches say something. Neither drops
        // somebody on Home having tapped Accept and watched nothing happen,
        // which is what a spent envelope and a silent redirect look like from
        // the outside — a broken link.
        // The "open your conversation" promise is only made when there is a
        // conversation to open — a connected pair whose thread is gone gets
        // the fact without the promise, and lands on Home.
        notify(
          t(result.chatId ? S.landingAlreadyTalking : S.landingAlreadyConnected),
        );
        router.replace(
          result.chatId ? `/chat/${result.chatId}` : "/(tabs)/home",
        );
        return;
      }
      // `unavailable`, and deliberately mute: a block, a cooldown, a Cue
      // already pending, a deactivated sender. Each is a fact about the other
      // person that this screen must not disclose, so it gives the same answer
      // every other surface gives.
      router.replace("/(tabs)/home");
    } catch (error) {
      // Re-read rather than trusting the snapshot: it may have been claimed by
      // somebody else, or expired, between the preview and the tap. The fresh
      // state carries the right sentence.
      const fresh = await invitesApi.preview(code).catch(() => null);
      if (fresh) setPreview(fresh);
      else if (error instanceof Error && error.message) {
        notify(t(S.landingMissing), error.message);
      }
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.centered}>
          <ActivityIndicator color={c.tint} />
        </View>
      </SafeAreaView>
    );
  }

  if (MOBILE_WEB) {
    // Signed in, the preview names the sender; signed out it cannot (the
    // endpoint is authenticated by design — sign in before seeing who), so
    // the invitation stays generic. Either way the one action is the app:
    // this URL is the app link itself, so once installed the same link opens
    // natively and the real flow runs there.
    const refusal =
      preview && preview.state !== "ok" ? REFUSAL_COPY[preview.state] : null;
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.centered}>
          <View style={styles.card}>
            {preview ? (
              <Avatar
                seed={preview.inviterAvatarSeed}
                name={preview.inviterName}
                size={72}
                accessibilityLabel={preview.inviterName}
              />
            ) : null}
            <Text style={styles.title}>
              {preview
                ? t(S.landingTitle, { name: preview.inviterName })
                : t(S.landingInvitedGeneric)}
            </Text>
            {refusal ? (
              <View style={styles.refusal}>
                <Ionicons
                  name="information-circle-outline"
                  size={18}
                  color={c.textMuted}
                />
                <Text style={styles.refusalText}>{t(refusal)}</Text>
              </View>
            ) : (
              <Text style={styles.body}>{t(S.landingMobileWeb)}</Text>
            )}
            <Pressable
              onPress={() =>
                void Linking.openURL(
                  landingOriginForHost(window.location.hostname),
                )
              }
              accessibilityRole="button"
              accessibilityLabel={t(S.landingGetApp)}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryText}>{t(S.landingGetApp)}</Text>
            </Pressable>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (missing || !preview) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.centered}>
          <Text style={styles.title}>{t(S.landingMissing)}</Text>
          <Pressable
            onPress={() => router.replace("/(tabs)/home")}
            accessibilityRole="button"
            style={styles.secondaryBtn}
          >
            <Text style={styles.secondaryText}>{t(S.landingBrowse)}</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.centered}>
        <View style={styles.card}>
          <Avatar
            seed={preview.inviterAvatarSeed}
            name={preview.inviterName}
            size={72}
            accessibilityLabel={preview.inviterName}
          />
          <Text style={styles.title}>
            {t(S.landingTitle, { name: preview.inviterName })}
          </Text>
          {/* Only a claimable link explains itself. A dead one — spent,
              expired, or your own — gets its one refusal sentence and nothing
              inviting anyone to accept. */}
          {preview.state === "ok" ? (
            <Text style={styles.body}>
              {t(preview.cueIntent ? S.landingCueBody : S.landingPlainBody)}
            </Text>
          ) : null}

          {preview.state === "ok" ? (
            <Pressable
              onPress={() => void accept()}
              disabled={accepting}
              accessibilityRole="button"
              accessibilityLabel={t(S.landingAccept)}
              style={[styles.primaryBtn, accepting && styles.btnBusy]}
            >
              {accepting ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.primaryText}>{t(S.landingAccept)}</Text>
              )}
            </Pressable>
          ) : (
            <View style={styles.refusal}>
              <Ionicons
                name="information-circle-outline"
                size={18}
                color={c.textMuted}
              />
              <Text style={styles.refusalText}>
                {t(REFUSAL_COPY[preview.state])}
              </Text>
            </View>
          )}

          <Pressable
            onPress={() => router.replace("/(tabs)/home")}
            accessibilityRole="button"
            style={styles.linkBtn}
          >
            <Text style={styles.linkText}>{t(S.landingBrowse)}</Text>
          </Pressable>
        </View>
      </View>
    </SafeAreaView>
  );
}

function createStyles(c: ThemeColors) {
  return {
    safe: { flex: 1, backgroundColor: c.background } as const,
    centered: {
      flex: 1,
      justifyContent: "center",
      paddingHorizontal: Spacing.lg,
    } as const,
    card: {
      alignItems: "center",
      gap: Spacing.sm,
      padding: Spacing.lg,
      borderRadius: Radius.lg,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surface,
    } as const,
    title: {
      color: c.text,
      fontSize: 20,
      fontWeight: "800",
      textAlign: "center",
    } as const,
    body: {
      color: c.textMuted,
      fontSize: 14,
      lineHeight: 20,
      textAlign: "center",
    } as const,
    primaryBtn: {
      alignSelf: "stretch",
      backgroundColor: c.tint,
      borderRadius: Radius.md,
      paddingVertical: 14,
      alignItems: "center",
      marginTop: Spacing.sm,
    } as const,
    primaryText: { color: "#fff", fontSize: 15, fontWeight: "800" } as const,
    btnBusy: { opacity: 0.7 } as const,
    secondaryBtn: {
      alignSelf: "stretch",
      borderColor: c.border,
      borderWidth: 1,
      borderRadius: Radius.md,
      paddingVertical: 12,
      alignItems: "center",
      marginTop: Spacing.sm,
    } as const,
    secondaryText: { color: c.text, fontSize: 14, fontWeight: "700" } as const,
    refusal: {
      flexDirection: "row",
      alignItems: "center",
      gap: Spacing.xs,
      marginTop: Spacing.sm,
    } as const,
    refusalText: { color: c.textMuted, fontSize: 13, flexShrink: 1 } as const,
    linkBtn: { alignItems: "center", paddingVertical: Spacing.sm } as const,
    linkText: { color: c.tint, fontSize: 14, fontWeight: "700" } as const,
  };
}
